import { useState } from "react";
import { FundraiserForAccount } from "../../../Interfaces/AccountUsers";
import "./DesktopTableView.css";
import {
  PARTICIPANT,
  COORDINATOR,
  REPRESENTATIVE,
  TEAM_ADMIN,
} from "../../../constants";

import {
  calculateProgressPercentage,
  useHandleViewFundraiser,
  getShareButtonText,
  displayFundraiserProfileUpload,
} from "../DashboardCommonFunctions";
import config from "../../../config";
import { ParticipantDashboardShareButton } from "../../../components/Storefront/ShareStoreComponent";

interface GeneralDashboardFundraiserDisplayProps {
  fundraiser: FundraiserForAccount[];
  isMobile: boolean;
}

const GeneralDashboardTableView: React.FC<
  GeneralDashboardFundraiserDisplayProps
> = ({ fundraiser, isMobile }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = isMobile ? 3 : 8;
  const totalPages = Math.ceil(fundraiser.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const displayedFundraisers = fundraiser.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleViewFundraiser = useHandleViewFundraiser();

  const StoreLink = ({ fundraiser }: { fundraiser: FundraiserForAccount }) => {
    const buttonStyle: React.CSSProperties = {
      backgroundColor: "red",
      color: "white",
      border: "none",
      borderRadius: "20px",
      padding: "0.5rem 1rem",
      fontSize: "1rem",
      fontWeight: "bold",
      cursor: "pointer",
      transition: "all 0.3s ease",
    };

    const isParticipant = fundraiser.fundraiser_type === PARTICIPANT;
    return (
      <div className="general-dashboard-fundraiser-table-view-store-link">
        {isParticipant && !isMobile && (
          <div className="general-dashboard-fundraiser-table-view-store-link-left">
            <p className="general-dashboard-fundraiser-table-view-store-link-label">
              Store Link
            </p>
            <p className="general-dashboard-fundraiser-table-view-store-link-value">
              {`${config.REACT_APP_SITE_URL}participant-shop/part-id/${fundraiser.code}`}
            </p>
          </div>
        )}
        <div className="general-dashboard-fundraiser-table-view-store-link-right">
          <div className="general-dashboard-fundraiser-table-view-store-code-section">
            <p className="general-dashboard-fundraiser-table-view-store-code-label">
              {isParticipant ? "Your Code" : "Fundraiser Code"}
            </p>
            <p className="general-dashboard-fundraiser-table-view-store-code-value">
              {fundraiser.code}
            </p>
          </div>
          <ParticipantDashboardShareButton
            fundraiserName={fundraiser.display_name}
            participantName={fundraiser.display_name}
            fundraiserCode={fundraiser.code}
            buttonText={getShareButtonText(fundraiser, true)}
            style={buttonStyle}
          />
        </div>
      </div>
    );
  };

  if (isMobile) {
    return (
      <div>
        <div className="general-dashboard-fundraiser-table-view-mobile">
          {displayedFundraisers.map((item) => {
            const progressPercent = calculateProgressPercentage(
              item.total_raised,
              item.total_goal,
            );
            return (
              <div
                // fundraiser id may be duplicated if multiple instances of a single fundraiser
                // IE are a coordinator and participant, need more specific to ensure uniqueness
                // just id and display name fail if they are a coordinator and team admin
                // just id and type fail if they have multiple participants
                key={`${item.fundraiser_id} - ${item.display_name} - ${item.fundraiser_type}`}
                className="general-dashboard-fundraiser-table-view-mobile-card"
              >
                <div className="general-dashboard-fundraiser-table-view-mobile-type-badge">
                  <span
                    className={`general-dashboard-fundraiser-table-view-mobile-badge general-dashboard-fundraiser-table-view-mobile-badge-${item.fundraiser_type}`}
                  >
                    {item.fundraiser_type}
                  </span>
                </div>
                <h4 className="general-dashboard-fundraiser-table-view-mobile-fundraiser-name">
                  {item.fundraiser_name}
                </h4>
                <p className="general-dashboard-fundraiser-table-view-mobile-org-name">
                  {item.display_name}
                </p>

                <div className="general-dashboard-fundraiser-table-view-mobile-amount-section">
                  <div>
                    <p className="general-dashboard-fundraiser-table-view-mobile-amount-label">
                      Raised
                    </p>
                    <p className="general-dashboard-fundraiser-table-view-mobile-amount">
                      ${item.total_raised.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="general-dashboard-fundraiser-table-view-mobile-goal-label">
                      Goal
                    </p>
                    <p className="general-dashboard-fundraiser-table-view-mobile-goal">
                      ${item.total_goal.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="general-dashboard-fundraiser-table-view-mobile-progress-bar">
                  <div
                    className="general-dashboard-fundraiser-table-view-mobile-progress-fill"
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                </div>

                <div className="general-dashboard-fundraiser-table-view-mobile-days-section">
                  <p className="general-dashboard-fundraiser-table-view-mobile-days">
                    {item.days_left} days left
                  </p>
                  <button
                    onClick={() => handleViewFundraiser(item)}
                    className="general-dashboard-fundraiser-table-view-mobile-action-btn"
                  >
                    {item.fundraiser_type === PARTICIPANT
                      ? "More details →"
                      : "Manage →"}
                  </button>
                </div>
                <StoreLink fundraiser={item} />
                {displayFundraiserProfileUpload({ fundraiser: item })}
              </div>
            );
          })}
        </div>
        {totalPages > 1 && (
          <div className="general-dashboard-fundraiser-table-view-pagination">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="general-dashboard-fundraiser-table-view-pagination-btn"
            >
              ← Previous
            </button>
            <span className="general-dashboard-fundraiser-table-view-pagination-info">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages - 1, currentPage + 1))
              }
              disabled={currentPage === totalPages - 1}
              className="general-dashboard-fundraiser-table-view-pagination-btn"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="general-dashboard-fundraiser-table-view-desktop">
        <table className="general-dashboard-fundraiser-table-view-table">
          <tbody>
            {displayedFundraisers.map((item) => {
              const progressPercent = calculateProgressPercentage(
                item.total_raised,
                item.total_goal,
              );
              const profileUploadContent = displayFundraiserProfileUpload({
                fundraiser: item,
              });
              return (
                <>
                  <tr
                    // fundraiser id may be duplicated if multiple instances of a single fundraiser
                    // IE are a coordinator and participant, need more specific to ensure uniqueness
                    // just id and display name fail if they are a coordinator and team admin
                    // just id and type fail if they have multiple participants
                    key={`${item.fundraiser_id} - ${item.display_name} - ${item.fundraiser_type}`}
                    className="general-dashboard-fundraiser-table-view-row"
                  >
                    <td className="general-dashboard-fundraiser-table-view-cell-fundraiser">
                      <div className="general-dashboard-fundraiser-table-view-fundraiser-info">
                        <span
                          className={`general-dashboard-fundraiser-table-view-badge general-dashboard-fundraiser-table-view-badge-${item.fundraiser_type}`}
                        >
                          {item.fundraiser_type}
                        </span>
                        <div className="general-dashboard-fundraiser-table-view-fundraiser-details">
                          <p className="general-dashboard-fundraiser-table-view-fundraiser-name">
                            {item.fundraiser_name}
                          </p>
                          <p className="general-dashboard-fundraiser-table-view-fundraiser-org">
                            {item.display_name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="general-dashboard-fundraiser-table-view-cell-progress">
                      <div className="general-dashboard-fundraiser-table-view-progress-container">
                        <div className="general-dashboard-fundraiser-table-view-progress-bar">
                          <div
                            className="general-dashboard-fundraiser-table-view-progress-fill"
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        </div>
                        <p className="general-dashboard-fundraiser-table-view-progress-text">
                          ${item.total_raised.toLocaleString()} / $
                          {item.total_goal.toLocaleString()}
                        </p>
                      </div>
                    </td>
                    <td className="general-dashboard-fundraiser-table-view-cell-days">
                      <p className="general-dashboard-fundraiser-table-view-days-text">
                        {item.days_left}
                      </p>
                      <p className="general-dashboard-fundraiser-table-view-days-label">
                        days left
                      </p>
                    </td>
                    <td className="general-dashboard-fundraiser-table-view-cell-action">
                      <button
                        onClick={() => handleViewFundraiser(item)}
                        className="general-dashboard-fundraiser-table-view-action-btn"
                      >
                        {item.fundraiser_type === PARTICIPANT
                          ? "More details"
                          : "Manage"}
                      </button>
                    </td>
                  </tr>
                  <tr className="general-dashboard-fundraiser-table-view-store-link-row">
                    <td colSpan={4}>
                      <StoreLink fundraiser={item} />
                    </td>
                  </tr>
                  {profileUploadContent && (
                    <tr className="general-dashboard-fundraiser-table-view-store-link-row">
                      <td colSpan={4}>{profileUploadContent}</td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="general-dashboard-fundraiser-table-view-pagination">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="general-dashboard-fundraiser-table-view-pagination-btn"
          >
            ← Previous
          </button>
          <span className="general-dashboard-fundraiser-table-view-pagination-info">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage(Math.min(totalPages - 1, currentPage + 1))
            }
            disabled={currentPage === totalPages - 1}
            className="general-dashboard-fundraiser-table-view-pagination-btn"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default GeneralDashboardTableView;
